import api from './api';

export type DocumentType =
  | 'document_front'
  | 'document_back'
  | 'resume'
  | 'profile_photo'
  | 'certificate';

export type DocumentStatus = 'pending' | 'approved' | 'rejected';

export interface CaregiverDocument {
  id: string;
  type: DocumentType;
  original_name: string | null;
  status: DocumentStatus;
  rejection_reason: string | null;
  // Signed URL valid for 5 minutes; null when the backend stores on disk
  url: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CaregiverDocumentsResponse {
  documents: CaregiverDocument[];
  missing: DocumentType[];
  complete: boolean;
  verified: boolean;
}

// Mirrors the backend limits in uploadDocuments middleware
export const MAX_FILE_SIZE_IN_MB = 10;
export const MAX_CERTIFICATES = 5;

const IMAGES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
const PDF = ['application/pdf'];
const WORD = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const ALLOWED_MIME_TYPES: Record<DocumentType, string[]> = {
  document_front: [...IMAGES, ...PDF],
  document_back: [...IMAGES, ...PDF],
  resume: [...PDF, ...WORD, ...IMAGES],
  profile_photo: ['image/jpeg', 'image/png', 'image/webp'],
  certificate: [...IMAGES, ...PDF],
};

export function validateDocumentFile(
  type: DocumentType,
  file: File,
): string | null {
  if (file.size > MAX_FILE_SIZE_IN_MB * 1024 * 1024) {
    return `Cada arquivo deve ter no máximo ${MAX_FILE_SIZE_IN_MB}MB.`;
  }

  // Some browsers report an empty type (e.g. HEIC); let the backend decide
  if (file.type && !ALLOWED_MIME_TYPES[type].includes(file.type)) {
    return 'Formato de arquivo não permitido.';
  }

  return null;
}

export async function listDocuments(): Promise<CaregiverDocumentsResponse> {
  const response = await api.get<CaregiverDocumentsResponse>('/documents');

  return response.data;
}

export async function uploadDocuments(
  type: DocumentType,
  files: File[],
): Promise<CaregiverDocumentsResponse> {
  const data = new FormData();

  files.forEach(file => data.append(type, file));

  const response = await api.post<CaregiverDocumentsResponse>(
    '/documents',
    data,
  );

  return response.data;
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`/documents/${id}`);
}
